import { Auth0Provider, useAuth0 } from '@auth0/auth0-react'
import { Center, Loader, Stack, Text } from '@mantine/core'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { BrowserRouter, Navigate, Route, Routes, useNavigate } from 'react-router-dom'
import { Layout } from './components/Layout/Layout'
import { ProtectedRoute } from './components/ProtectedRoute'
import { auth0Config, currentReturnTo } from './config/auth0.config'
import { apiClient } from './services/api'
import { VersionProvider } from './context/VersionContext'
import { TenantVersionGate } from './pages/TenantVersionGate'
import { usePermissions } from './hooks/usePermissions'

// Client-facing pages
import { OverviewDashboard } from './pages/OverviewDashboard'
import { ReportabilityDashboard } from './pages/ReportabilityDashboard'
import { IdentityMatchDashboard } from './pages/IdentityMatchDashboard'
import { RoutingDashboard } from './pages/RoutingDashboard'
import { SearchDashboard } from './pages/SearchDashboard'
import { OffenseRecordsPage } from './pages/OffenseRecordsPage'
import { ProductAutomationSummaryPage } from './pages/ProductAutomationSummaryPage'
import { RoutingAccuracyPage } from './pages/RoutingAccuracyPage'

// Operations pages (internal)
import { FeatureCombinationQAPage } from './pages/FeatureCombinationQAPage'
import QaErrorRateDashboard from './pages/QaErrorRateDashboard'
import { ExternalQAPage } from './pages/ExternalQAPage'
import { ExpertFeedbackPage } from './pages/ExpertFeedbackPage'
import { LabelingPage } from './pages/LabelingPage'
import { BusinessMetricsDashboard } from './pages/BusinessMetricsDashboard'
import { MeteringDashboard } from './pages/MeteringDashboard'
import { ClientOnboardingPage } from './pages/ClientOnboardingPage'
import { EvaluateRecordPage } from './pages/EvaluateRecordPage'
import { CompareSearchesPage } from './pages/CompareSearchesPage'

function ExpertIndexRedirect() {
  const { userRole } = usePermissions()
  if (userRole === 'Public-Records-Expert') {
    return <Navigate to="/offenses" replace />
  }
  return <OverviewDashboard />
}

const auth0TokenParams = {
  authorizationParams: {
    audience: auth0Config.authorizationParams.audience,
    scope: auth0Config.authorizationParams.scope,
  },
}

function AuthSessionGate({ children }: { children: ReactNode }) {
  const { getAccessTokenSilently, isAuthenticated, loginWithRedirect } = useAuth0()
  const queryClient = useQueryClient()
  const [tokenReady, setTokenReady] = useState(false)
  const redirectingRef = useRef(false)

  const redirectToLogin = useCallback(() => {
    if (redirectingRef.current) return
    redirectingRef.current = true
    void loginWithRedirect({
      appState: { returnTo: currentReturnTo() },
      authorizationParams: { screen_hint: 'login' },
    })
  }, [loginWithRedirect])

  const fetchToken = useCallback(async (cacheMode: 'on' | 'off' = 'on') => {
    return getAccessTokenSilently({ ...auth0TokenParams, cacheMode })
  }, [getAccessTokenSilently])

  useEffect(() => {
    if (!isAuthenticated) {
      setTokenReady(false)
      apiClient.setAuth0TokenGetter(null)
      apiClient.setUnauthorizedHandler(null)
      return
    }

    const getToken = async () => {
      try {
        return await fetchToken('on')
      } catch {
        return await fetchToken('off')
      }
    }

    apiClient.setAuth0TokenGetter(async () => {
      try {
        return await getToken()
      } catch (error) {
        redirectToLogin()
        throw error
      }
    })

    apiClient.setUnauthorizedHandler(async (event) => {
      if (event === 'give-up') {
        await queryClient.cancelQueries()
        redirectToLogin()
        return
      }
      try {
        await fetchToken('off')
      } catch {
        await queryClient.cancelQueries()
        redirectToLogin()
      }
    })

    let cancelled = false
    getToken()
      .then(() => {
        if (!cancelled) setTokenReady(true)
      })
      .catch(() => {
        if (!cancelled) redirectToLogin()
      })

    return () => {
      cancelled = true
    }
  }, [fetchToken, isAuthenticated, queryClient, redirectToLogin])

  if (!tokenReady) {
    return (
      <Center h="100vh">
        <Stack align="center" gap="md">
          <Loader size="lg" />
          <Text size="sm" c="dimmed">Signing you in...</Text>
        </Stack>
      </Center>
    )
  }

  return <>{children}</>
}

function Auth0ProviderWithNavigate({ children }: { children: ReactNode }) {
  const navigate = useNavigate()
  return (
    <Auth0Provider
      domain={auth0Config.domain}
      clientId={auth0Config.clientId}
      authorizationParams={auth0Config.authorizationParams}
      useRefreshTokens={auth0Config.useRefreshTokens}
      cacheLocation={auth0Config.cacheLocation}
      useRefreshTokensFallback={auth0Config.useRefreshTokensFallback}
      onRedirectCallback={(appState) => {
        navigate(appState?.returnTo || currentReturnTo() || '/', { replace: true })
      }}
    >
      {children}
    </Auth0Provider>
  )
}

function App() {
  return (
    <BrowserRouter>
      <Auth0ProviderWithNavigate>
        <Routes>
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <AuthSessionGate>
                  <VersionProvider>
                    <TenantVersionGate />
                  </VersionProvider>
                </AuthSessionGate>
              </ProtectedRoute>
            }
          >
            <Route element={<Layout />}>
            {/* Client-facing analytics */}
            <Route index element={<ExpertIndexRedirect />} />
            <Route path="compliance" element={<ReportabilityDashboard />} />
            <Route path="offenses" element={<OffenseRecordsPage />} />
            <Route path="identity" element={<IdentityMatchDashboard />} />
            <Route path="routing" element={<RoutingDashboard />} />
            <Route path="search" element={<SearchDashboard />} />
            <Route path="automation" element={<ProductAutomationSummaryPage />} />
            <Route path="metering" element={<MeteringDashboard />} />

            {/* Operations (internal) */}
            <Route path="ops/feature-qa" element={<FeatureCombinationQAPage />} />
            <Route path="ops/qa-errors" element={<QaErrorRateDashboard />} />
            <Route path="ops/expert-feedback" element={<ExpertFeedbackPage />} />
            <Route path="ops/external-qa" element={<ExternalQAPage />} />
            <Route path="ops/labeling" element={<LabelingPage />} />
            <Route path="ops/evaluate" element={<EvaluateRecordPage />} />
            <Route path="ops/compare-searches" element={<CompareSearchesPage />} />
            <Route path="ops/business-metrics" element={<BusinessMetricsDashboard />} />
            <Route
              path="ops/client-onboarding"
              element={(
                <ProtectedRoute requiredPermission="manage:settings">
                  <ClientOnboardingPage />
                </ProtectedRoute>
              )}
            />

            {/* Legacy redirects */}
            <Route path="reportability" element={<Navigate to="/compliance" replace />} />
            <Route path="identity-match" element={<Navigate to="/identity" replace />} />
            <Route path="search-dashboard" element={<Navigate to="/search" replace />} />
            <Route path="product-automation" element={<Navigate to="/automation" replace />} />
            <Route path="executive-summary" element={<Navigate to="/" replace />} />
            <Route path="summary" element={<Navigate to="/" replace />} />
            <Route path="feature-qa" element={<Navigate to="/ops/feature-qa" replace />} />
            <Route path="qa-error-rate" element={<Navigate to="/ops/qa-errors" replace />} />
            <Route path="expert-feedback" element={<Navigate to="/ops/expert-feedback" replace />} />
            <Route path="labeling" element={<Navigate to="/ops/labeling" replace />} />
            <Route path="external-qa" element={<Navigate to="/ops/external-qa" replace />} />
            <Route path="business-metrics" element={<Navigate to="/ops/business-metrics" replace />} />
            <Route path="client-onboarding" element={<Navigate to="/ops/client-onboarding" replace />} />
            <Route path="routing-accuracy" element={<RoutingAccuracyPage />} />
            <Route path="qa-accuracy" element={<Navigate to="/routing-accuracy" replace />} />

            <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Route>
        </Routes>
      </Auth0ProviderWithNavigate>
    </BrowserRouter>
  )
}

export default App
